import { getSetting, setSetting, removeSetting } from "../office/storage";
import { BACKEND_URL } from "../config";

const CLIENT_ID = "ff8f1729-0b38-4f26-9511-41ace4375d1e";
const SCOPES = ["User.Read", "Mail.Read", "openid", "profile", "offline_access"];
const TOKEN_ENDPOINT = "https://login.microsoftonline.com/common/oauth2/v2.0/token";
const AUTH_ENDPOINT = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

// Must exactly match an SPA redirect URI registered on the Azure app.
const REDIRECT_URI = `${window.location.origin}/authDialog.html`;

export interface AuthAccount {
  name: string;
  username: string;
}

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function b64url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/=/g, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
}

async function generateVerifier(): Promise<string> {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return b64url(arr.buffer);
}

async function codeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  return b64url(await crypto.subtle.digest("SHA-256", data));
}

// ── Token storage (Office roamingSettings, falls back to localStorage) ───────

const STORAGE_KEY = "aslan_auth";
const APP_SESSION_KEY = "aslan_app";

export type AppRole = "super_admin" | "org_admin" | "user";

/** Thrown when the Aslan-authenticated identity differs from the Outlook
 *  mailbox owner. Message is prefixed with ACCOUNT_MISMATCH: so the login view
 *  can show it verbatim instead of the generic failure text. */
export class IdentityMismatchError extends Error {}

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: AppRole;
}

interface StoredTokens {
  access_token: string;
  refresh_token?: string;
  id_token: string;
  expires_at: number;
  account: AuthAccount;
}

// Backend-issued app session (JWT) obtained by exchanging the MS id_token.
interface AppSession {
  appToken: string;
  expires_at: number;
  user: AppUser;
}

function saveTokens(tokens: StoredTokens): Promise<void> {
  return setSetting(STORAGE_KEY, tokens);
}

function loadTokens(): StoredTokens | null {
  return getSetting<StoredTokens>(STORAGE_KEY);
}

function clearTokens(): Promise<void> {
  return removeSetting(STORAGE_KEY);
}

function loadAppSession(): AppSession | null {
  return getSetting<AppSession>(APP_SESSION_KEY);
}

// An Office SSO bootstrap token for the user signed into Outlook, or null when
// SSO is unavailable (dev / unsupported host / not yet configured in Azure).
// The backend validates it and enforces that its identity matches the sign-in —
// a tamper-proof guarantee the add-in is acting as the actual mailbox owner.
async function getOfficeSsoToken(): Promise<string | null> {
  try {
    const auth = typeof Office !== "undefined" ? Office?.auth : undefined;
    if (!auth || typeof auth.getAccessToken !== "function") return null;
    return await auth.getAccessToken({ allowSignInPrompt: true, allowConsentPrompt: true });
  } catch (e) {
    // 13xxx errors (consent needed, unsupported host/account, etc.) — degrade
    // gracefully; the backend falls back to the id_token identity + client-side
    // guard. Log the code: without it a null ssoToken is indistinguishable from
    // "SSO not configured", which makes REQUIRE_OFFICE_SSO impossible to verify.
    // Common: 13001 not signed in, 13002 consent declined, 13003 unsupported
    // account (personal MSA), 13007 Azure app misconfigured, 13012 unsupported host.
    const err = e as { code?: number; message?: string };
    /* eslint-disable-next-line no-console */
    console.warn("[sso] getAccessToken failed", err?.code ?? "?", err?.message ?? e);
    return null;
  }
}

// Exchange a Microsoft id_token for the backend app JWT (carries role + userId).
async function exchangeForAppToken(idToken: string): Promise<AppSession> {
  const ssoToken = await getOfficeSsoToken();
  const res = await fetch(`${BACKEND_URL}/api/auth/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken, ssoToken }),
  });
  const json = await res.json();
  if (!res.ok || !json.appToken) {
    // A provisioning rejection (403) returns detail = {code, message}; a plain
    // auth failure returns a string. Surface a human message either way so an
    // uninvited / disabled user sees why instead of "[object Object]".
    const d = json?.detail;
    const message =
      typeof d === "string" ? d : d?.message ?? "Backend session exchange failed";
    throw new Error(message);
  }
  const session: AppSession = {
    appToken: json.appToken,
    expires_at: Date.now() + (json.expiresInMin ?? 60) * 60_000,
    user: json.user,
  };
  await setSetting(APP_SESSION_KEY, session);
  return session;
}

// ── Token exchange / refresh ──────────────────────────────────────────────────

async function exchangeCode(
  code: string,
  verifier: string,
  redirectUri: string,
): Promise<StoredTokens> {
  const res = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: CLIENT_ID,
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
      scope: SCOPES.join(" "),
    }),
  });

  const json = await res.json();
  if (!json.access_token) {
    throw new Error(json.error_description ?? "Token exchange failed");
  }

  const payload = JSON.parse(atob(json.id_token.split(".")[1]));
  const account: AuthAccount = {
    name: payload.name ?? payload.preferred_username,
    username: payload.preferred_username,
  };

  return {
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    id_token: json.id_token,
    expires_at: Date.now() + json.expires_in * 1000,
    account,
  };
}

async function refreshToken(stored: StoredTokens): Promise<StoredTokens | null> {
  if (!stored.refresh_token) return null;
  try {
    const res = await fetch(TOKEN_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: "refresh_token",
        refresh_token: stored.refresh_token,
        scope: SCOPES.join(" "),
      }),
    });
    const json = await res.json();
    if (!json.access_token) return null;

    const updated: StoredTokens = {
      ...stored,
      access_token: json.access_token,
      refresh_token: json.refresh_token ?? stored.refresh_token,
      // openid scope is requested, so a fresh id_token comes back on refresh.
      id_token: json.id_token ?? stored.id_token,
      expires_at: Date.now() + json.expires_in * 1000,
    };
    await saveTokens(updated);
    return updated;
  } catch {
    return null;
  }
}

// ── Office dialog auth ────────────────────────────────────────────────────────

interface DialogMessage {
  code?: string;
  error?: string;
}

function openAuthDialog(authUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error?.message ?? "Could not open auth dialog"));
          return;
        }

        const dialog = result.value;
        let settled = false;

        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          try { dialog.close(); } catch { /* already closed */ }
          fn();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          const messageArg = arg as { message: string };
          let parsed: DialogMessage;
          try {
            parsed = JSON.parse(messageArg.message) as DialogMessage;
          } catch {
            finish(() => reject(new Error("Auth dialog returned invalid payload")));
            return;
          }
          if (parsed.error) {
            finish(() => reject(new Error(parsed.error!)));
            return;
          }
          if (!parsed.code) {
            finish(() => reject(new Error("Auth dialog returned no code")));
            return;
          }
          finish(() => resolve(parsed.code!));
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          // 12006 = user closed the dialog. Other codes are network/render errors.
          const errArg = arg as { error: number };
          if (errArg.error === 12006) {
            finish(() => reject(new Error("user_cancelled")));
          } else {
            finish(() => reject(new Error(`Auth dialog error ${errArg.error}`)));
          }
        });
      },
    );
  });
}

// ── Mailbox-identity guard ────────────────────────────────────────────────────

/** The email of the Outlook mailbox the add-in is running in (the host user),
 *  lowercased. null when not running inside Outlook (dev / standalone). */
function mailboxOwnerEmail(): string | null {
  try {
    const email =
      typeof Office !== "undefined"
        ? Office?.context?.mailbox?.userProfile?.emailAddress
        : undefined;
    return email ? email.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Enforce that the Aslan-authenticated email matches the Outlook mailbox owner.
 *  The add-in must act as the signed-in Outlook user — signing into Aslan with a
 *  different account would score/generate against the wrong identity. No-op when
 *  the mailbox email is unavailable (dev / non-Outlook host). */
function assertMailboxIdentity(appEmail: string): void {
  const owner = mailboxOwnerEmail();
  if (!owner) return;
  if (owner !== (appEmail || "").trim().toLowerCase()) {
    throw new IdentityMismatchError(
      `ACCOUNT_MISMATCH:You're signed in to Outlook as ${owner}, but tried to use Aslan AI as ${appEmail}. ` +
        `Please sign in to Aslan AI with ${owner}.`,
    );
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Returns the signed-in account if a valid (or refreshable) token exists. */
export async function initAuth(): Promise<AuthAccount | null> {
  const stored = loadTokens();
  if (!stored) return null;

  let current = stored;
  if (Date.now() >= stored.expires_at - 300_000) {
    const refreshed = await refreshToken(stored);
    if (!refreshed) return null;
    current = refreshed;
  }

  // Best-effort: make sure we hold a backend app session for API calls.
  try {
    await getAppToken();
  } catch {
    /* backend unreachable — the UI still loads; API calls surface the error */
  }

  // A stored session must belong to the current Outlook mailbox owner. If the
  // user switched Outlook accounts (or signed in with a different one), drop the
  // stale session and force a fresh, matching sign-in.
  const appUser = getCurrentAppUser();
  if (appUser) {
    try {
      assertMailboxIdentity(appUser.email);
    } catch {
      await logout();
      return null;
    }
  }
  return current.account;
}

/** Returns a valid Microsoft id_token, refreshing the MS session if needed. */
async function getIdToken(): Promise<string> {
  const stored = loadTokens();
  if (!stored) throw new Error("Not signed in");
  if (Date.now() < stored.expires_at - 300_000) return stored.id_token;

  const refreshed = await refreshToken(stored);
  if (!refreshed) throw new Error("Session expired — please sign in again");
  return refreshed.id_token;
}

/** Returns a valid backend app token, re-exchanging the id_token when expired. */
export async function getAppToken(): Promise<string> {
  const session = loadAppSession();
  if (session && Date.now() < session.expires_at - 60_000) return session.appToken;

  const idToken = await getIdToken();
  const refreshed = await exchangeForAppToken(idToken);
  return refreshed.appToken;
}

/** The current backend user (id/email/name/role), or null if no app session. */
export function getCurrentAppUser(): AppUser | null {
  return loadAppSession()?.user ?? null;
}

/** Opens the Microsoft login dialog via Office.context.ui and returns the account. */
export async function loginWithMicrosoft(): Promise<AuthAccount> {
  const verifier = await generateVerifier();
  const challenge = await codeChallenge(verifier);
  const state = crypto.randomUUID();

  const authUrl = new URL(AUTH_ENDPOINT);
  authUrl.searchParams.set("client_id", CLIENT_ID);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("redirect_uri", REDIRECT_URI);
  authUrl.searchParams.set("scope", SCOPES.join(" "));
  authUrl.searchParams.set("code_challenge", challenge);
  authUrl.searchParams.set("code_challenge_method", "S256");
  authUrl.searchParams.set("state", state);
  // Steer the dialog to the Outlook mailbox owner. With a hint we don't force
  // the account picker (which is what lets users pick the wrong account); when
  // the host isn't Outlook (dev) there's no hint, so fall back to the picker.
  const owner = mailboxOwnerEmail();
  if (owner) {
    authUrl.searchParams.set("login_hint", owner);
  } else {
    authUrl.searchParams.set("prompt", "select_account");
  }

  const code = await openAuthDialog(authUrl.toString());

  const tokens = await exchangeCode(code, verifier, REDIRECT_URI);
  await saveTokens(tokens);
  // Establish the backend app session immediately so the first API call works.
  const session = await exchangeForAppToken(tokens.id_token);
  // The account chosen in the Microsoft dialog must be the Outlook mailbox owner.
  // If they differ, tear down the just-created session so nothing is usable under
  // the wrong identity, and surface a clear message on the login screen.
  try {
    assertMailboxIdentity(session.user.email);
  } catch (e) {
    await logout();
    throw e;
  }
  return tokens.account;
}

/** Returns a valid access token, refreshing silently if needed. */
export async function getAccessToken(): Promise<string> {
  const stored = loadTokens();
  if (!stored) throw new Error("Not signed in");

  if (Date.now() < stored.expires_at - 300_000) return stored.access_token;

  const refreshed = await refreshToken(stored);
  if (!refreshed) throw new Error("Session expired — please sign in again");
  return refreshed.access_token;
}

/** Clears all stored auth state (MS tokens + backend app session). */
export async function logout(): Promise<void> {
  await clearTokens();
  await removeSetting(APP_SESSION_KEY);
}
